import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Dialog, Typography, keyframes } from '@mui/material';
import { EmojiEvents as TrophyIcon } from '@mui/icons-material';
import confetti from 'canvas-confetti';
import ConfiguredWheelFace, {
  isPlaceholderSliceLabel,
  sliceHasArt,
} from './ConfiguredWheelFace';
import PrizeCircleImage, { prizeModalImageUrl } from './PrizeCircleImage';
import DefaultStyleWheel from './DefaultStyleWheel';

const getDisplayName = (entry) => {
  if (entry.display_name && entry.display_name.trim()) return entry.display_name.trim();
  if (entry.email) {
    const local = entry.email.split('@')[0];
    return local.charAt(0).toUpperCase() + local.slice(1).replace(/[._]/g, ' ');
  }
  return `User ${entry.id || ''}`;
};

let labelMeasureCtx = null;
const measureLabelWidth = (text, fontSize) => {
  const label = String(text || '');
  const fallback = label.length * fontSize * 0.62;
  if (typeof document === 'undefined') return fallback;
  if (!labelMeasureCtx) labelMeasureCtx = document.createElement('canvas').getContext('2d');
  labelMeasureCtx.font = `800 ${fontSize}px sans-serif`;
  return Math.max(labelMeasureCtx.measureText(label).width, fallback);
};

/** Name runs hub → rim inside the slice. Long names use a smaller size so they stay in that lane. */
const radialSliceLabel = (name, size, sliceAngle) => {
  const hubR = size * 0.07 + 14;
  const outerR = size / 2 - 18;
  const halfRad = Math.min(Math.max((sliceAngle * Math.PI) / 360, 0.15), 1.15);
  const tanHalf = Math.tan(halfRad);
  const preferred = Math.min(15, Math.max(11, size * 0.034));
  const radiusForFont = preferred / (2 * tanHalf * 0.78);
  const innerR = Math.max(hubR, Math.min(radiusForFont, (hubR + outerR) * 0.42));
  const band = Math.max(28, outerR - innerR);
  const midRadius = innerR + band / 2;
  const sideCap = 2 * innerR * tanHalf * 0.78;
  let fontSize = Math.min(preferred, Math.max(7, sideCap));
  const label = String(name || '');
  while (fontSize > 6 && measureLabelWidth(label, fontSize) > band * 0.92) {
    fontSize -= 0.5;
  }
  return { fontSize, midRadius, band };
};

const pulseGlow = keyframes`
  0%,100% { box-shadow: 0 0 30px 8px rgba(255,215,0,0.25); }
  50%     { box-shadow: 0 0 60px 16px rgba(255,215,0,0.55); }
`;

const bounce = keyframes`
  0%,100% {
    filter: brightness(1);
    text-shadow: 0 1px 4px rgba(0,0,0,0.7);
  }
  50% {
    filter: brightness(1.25);
    text-shadow: 0 2px 8px rgba(255,215,0,0.55), 0 1px 4px rgba(0,0,0,0.7);
  }
`;

const LiveWheelDisplay = ({
  entries = [],
  spinning = false,
  finalAngle = 0,
  spinDuration = 6,
  winnerEntry = null,
  animationComplete = false,
  onAnimationEnd,
  size = 420,
  centerLogoSrc = '/mind_your_music.png',
  hotSeatPlayerName = null,
  onWinnerReveal,
  backgroundImageUrl = '',
  /** "default" uses the solo prize wheel so slice adjustments match. */
  variant = 'live',
  prizeIndex = 0,
  /** Prize palette: ease in, run fast, crawl onto the slice. */
  suspenseSpin = false,
  /** Pause on the landed slice before the prize modal. */
  revealDelayMs = 0,
  onLanded,
  /** When false, the landed "Prize" lines stay out of the wheel so the parent can place them. */
  showResultCaption = true,
}) => {
  const [currentRotation, setCurrentRotation] = useState(0);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [awaitingReveal, setAwaitingReveal] = useState(false);
  /** One celebration (modal + confetti) per spin; reset when a new spin starts */
  const winnerCelebrationShownRef = useRef(false);
  const winnerEntryRef = useRef(winnerEntry);
  const onAnimationEndRef = useRef(onAnimationEnd);
  const onWinnerRevealRef = useRef(onWinnerReveal);
  const onLandedRef = useRef(onLanded);
  const revealDelayRef = useRef(revealDelayMs);
  const holdTimerRef = useRef(null);
  const wasSpinningRef = useRef(false);
  winnerEntryRef.current = winnerEntry;
  onAnimationEndRef.current = onAnimationEnd;
  onWinnerRevealRef.current = onWinnerReveal;
  onLandedRef.current = onLanded;
  revealDelayRef.current = revealDelayMs;
  const isDefaultStyle = variant === 'default';

  const eligible = entries.filter((e) => !e.is_excluded);
  const count = eligible.length;
  const usesConfiguredArt =
    !!backgroundImageUrl || eligible.some((entry) => sliceHasArt(entry));
  const prizeLabel = (entry) => {
    if (!entry) return '';
    const name = getDisplayName(entry);
    if (sliceHasArt(entry) && isPlaceholderSliceLabel(name)) return '';
    if (backgroundImageUrl && isPlaceholderSliceLabel(name) && !entry.email) return '';
    return name;
  };

  // Build the conic-gradient segments + slice data
  const sliceAngle = count > 0 ? 360 / count : 360;
  const conicStops = eligible
    .map((e, i) => {
      const start = i * sliceAngle;
      const end = start + sliceAngle;
      const base = e.slice_color || '#FF6B6B';
      return `${base} ${start}deg ${end}deg`;
    })
    .join(', ');

  // Spin animation via CSS transition (fixed duration for host + user parity)
  useEffect(() => {
    const ready = Boolean(spinning && (finalAngle || isDefaultStyle));
    const started = ready && !wasSpinningRef.current;
    wasSpinningRef.current = ready;
    if (!started) return undefined;
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    winnerCelebrationShownRef.current = false;
    setAwaitingReveal(false);
    setShowWinnerModal(false);
    if (!isDefaultStyle) {
      setCurrentRotation((prev) => prev % 360);
      requestAnimationFrame(() => {
        setCurrentRotation(finalAngle);
      });
    }
    return undefined;
  }, [spinning, finalAngle, isDefaultStyle]);

  useEffect(() => () => {
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
  }, []);

  const fireConfetti = useCallback(() => {
    const dur = 2500;
    const end = Date.now() + dur;
    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 60 + Math.random() * 60,
        spread: 60,
        origin: { x: Math.random(), y: Math.random() * 0.6 },
        colors: ['#FFD700', '#FF6B6B', '#4ECDC4', '#fff'],
        // Above the prize overlay blur (2000) and the winner dialog (2600).
        zIndex: 12000,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, []);

  const revealWinner = useCallback(() => {
    const winner = winnerEntryRef.current;
    if (winner && !winnerCelebrationShownRef.current) {
      winnerCelebrationShownRef.current = true;
      setShowWinnerModal(true);
      fireConfetti();
      onWinnerRevealRef.current?.();
    }
    onAnimationEndRef.current?.();
  }, [fireConfetti]);

  const completeDefaultSpin = useCallback(() => {
    const delay = Math.max(0, Number(revealDelayRef.current) || 0);
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (!delay) {
      setAwaitingReveal(false);
      revealWinner();
      return;
    }
    setAwaitingReveal(true);
    onLandedRef.current?.();
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      setAwaitingReveal(false);
      revealWinner();
    }, delay);
  }, [revealWinner]);

  // End spin based on exact duration (prevents early host completion)
  useEffect(() => {
    if (!spinning || isDefaultStyle) return undefined;
    const timer = setTimeout(() => {
      setCurrentRotation((prev) => prev % 360);
      if (winnerEntry && !winnerCelebrationShownRef.current) {
        winnerCelebrationShownRef.current = true;
        setShowWinnerModal(true);
        fireConfetti();
        onWinnerReveal?.();
      }
      onAnimationEnd?.();
    }, Math.max(0, spinDuration) * 1000 + 40);
    return () => clearTimeout(timer);
  }, [spinning, isDefaultStyle, spinDuration, winnerEntry, onAnimationEnd, fireConfetti, onWinnerReveal]);

  // Auto-close winner modal after 5 seconds
  useEffect(() => {
    if (showWinnerModal) {
      const t = setTimeout(() => setShowWinnerModal(false), 5000);
      return () => clearTimeout(t);
    }
  }, [showWinnerModal]);

  // If host state marks complete before the local timer (e.g. WS race), show modal once
  useEffect(() => {
    if (animationComplete && winnerEntry && !winnerCelebrationShownRef.current) {
      winnerCelebrationShownRef.current = true;
      setShowWinnerModal(true);
      fireConfetti();
      onWinnerReveal?.();
    }
  }, [animationComplete, winnerEntry, fireConfetti, onWinnerReveal]);

  const winnerName = winnerEntry ? prizeLabel(winnerEntry) : '';
  const winnerPicture = prizeModalImageUrl(winnerEntry);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
        }}
      >
      {isDefaultStyle ? (
        <DefaultStyleWheel
          slices={eligible}
          logoUrl={centerLogoSrc}
          maxSize={size}
          mustStartSpinning={spinning}
          prizeNumber={Math.max(0, Math.min(prizeIndex, Math.max(count - 1, 0)))}
          onStopSpinning={completeDefaultSpin}
          spinning={spinning}
          suspenseSpin={suspenseSpin}
        />
      ) : (
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          mx: 'auto',
          flexShrink: 0,
          animation: animationComplete && winnerEntry ? `${pulseGlow} 1.8s ease-in-out infinite` : 'none',
          border: '6px solid rgba(6,6,6,0.2)',
          borderRadius: '50%'
        }}
      >
        {/* Pointer arrow at top centre */}
        <Box
          sx={{
            position: 'absolute',
            top: -18,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 20,
            width: 0,
            height: 0,
            borderLeft: '14px solid transparent',
            borderRight: '14px solid transparent',
            borderTop: '28px solid #FFD700',
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))',
          }}
        />

        {/* Outer ring glow */}
        <Box
          sx={{
            position: 'absolute',
            inset: -6,
            borderRadius: '50%',
            background: 'conic-gradient(from 0deg, rgba(255,215,0,0.18), rgba(255,100,100,0.12), rgba(78,205,196,0.12), rgba(255,215,0,0.18))',
            filter: 'blur(4px)',
            zIndex: 0,
          }}
        />

        {/* Outer border */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '6px solid rgba(60,60,60,0.9)',
            boxShadow: 'inset 0 0 20px rgba(0,0,0,0.4), 0 0 30px rgba(0,0,0,0.3)',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />

        {/* Spinning wheel container */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 1,
            transform: `rotate(${currentRotation}deg)`,
            transition: spinning
              ? `transform ${spinDuration}s cubic-bezier(0.17, 0.67, 0.12, 0.99)`
              : 'none',
            background: usesConfiguredArt
              ? '#1a1a1a'
              : count > 0
                ? `conic-gradient(${conicStops})`
                : 'radial-gradient(circle, #333, #1a1a1a)',
          }}
        >
          {usesConfiguredArt && count > 0 && (
            <Box sx={{ position: 'absolute', inset: 0 }}>
              <ConfiguredWheelFace
                slices={eligible.map((entry) => ({
                  color: entry.slice_color,
                  text: entry.display_name,
                  background_image_url: entry.background_image_url,
                  image_url: entry.image_url,
                  image_zoom: entry.image_zoom,
                  image_rotation: entry.image_rotation,
                  image_offset_x: entry.image_offset_x,
                  image_offset_y: entry.image_offset_y,
                }))}
                backgroundImageUrl={backgroundImageUrl}
                size={size}
                framed={false}
                fill
              />
            </Box>
          )}
          {count === 0 && (
            <Typography
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%,-50%)',
                color: '#666',
                fontWeight: 600,
                fontSize: 14,
                textAlign: 'center',
                width: '70%',
              }}
            >
              No participants
            </Typography>
          )}

          {/* Slice labels — configured art wheels draw their own labels */}
          {!usesConfiguredArt && eligible.map((entry, i) => {
            const angle = i * sliceAngle + sliceAngle / 2;
            const isWin = winnerEntry?.id === entry.id && animationComplete;
            const name = getDisplayName(entry);
            const { fontSize, midRadius, band } = radialSliceLabel(name, size, sliceAngle);

            return (
              <Box
                key={entry.id}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: 0,
                  height: 0,
                  transform: `rotate(${angle}deg)`,
                  pointerEvents: 'none',
                }}
              >
                {/* Name reads from the hub outward along the slice */}
                <Typography
                  component="span"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: band,
                    m: 0,
                    p: 0,
                    display: 'block',
                    transform: `translate(-50%, -50%) translateY(-${midRadius}px) rotate(-90deg)`,
                    transformOrigin: 'center center',
                    color: '#fff',
                    fontWeight: isWin ? 800 : 700,
                    fontSize,
                    fontFamily: 'sans-serif',
                    textShadow: '0 1px 4px rgba(0,0,0,0.85)',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                    lineHeight: 1,
                    background: isWin ? 'rgba(255,215,0,0.18)' : 'transparent',
                    borderRadius: 1,
                    animation: isWin ? `${bounce} 0.8s ease-in-out infinite` : 'none',
                  }}
                >
                  {name}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Centre button */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            width: size * 0.14,
            height: size * 0.14,
            borderRadius: '50%',
            background: 'radial-gradient(circle, #2a2a40 0%, #1a1a2e 100%)',
            border: '3px solid rgba(255,255,255,0.3)',
            boxShadow: '0 0 16px rgba(0,0,0,0.5)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="img"
            src={centerLogoSrc || '/mind_your_music.png'}
            alt="Logo"
            sx={{ width: '75%', height: '75%', objectFit: 'contain', borderRadius: '50%' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </Box>

        {/* Radial lines */}
        {!usesConfiguredArt && eligible.map((_, i) => (
          <Box
            key={`line-${i}`}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 1,
              height: size / 2 - 6,
              // background: 'rgba(0,0,0,0.15)',
              transformOrigin: 'top center',
              transform: `rotate(${i * sliceAngle + currentRotation}deg)`,
              zIndex: 3,
              pointerEvents: 'none',
              transition: spinning
                ? `transform ${spinDuration}s cubic-bezier(0.17, 0.67, 0.12, 0.99)`
                : 'none',
            }}
          />
        ))}
      </Box>
      )}

      {showResultCaption && !spinning && !awaitingReveal && winnerEntry && (
        <Box
          sx={{
            textAlign: 'center',
            mt: 2,
            px: 1.5,
            width: '100%',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              color: '#FFD700',
              fontWeight: 700,
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              textShadow: '0 1px 8px rgba(0,0,0,0.45)',
              display: 'block',
              lineHeight: 1.35,
            }}
          >
            {hotSeatPlayerName ? 'Prize' : 'Winner'}
            {winnerName ? `: ${winnerName}` : ''}
          </Typography>
          {hotSeatPlayerName ? (
            <Typography
              sx={{
                display: 'block',
                color: 'rgba(255,255,255,0.75)',
                fontWeight: 500,
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                lineHeight: 1.4,
              }}
            >
              For {hotSeatPlayerName}
            </Typography>
          ) : null}
        </Box>
      )}
      </Box>

      {/* Winner celebration modal */}
      <Dialog
        open={showWinnerModal}
        onClose={() => setShowWinnerModal(false)}
        fullWidth
        maxWidth="xs"
        sx={{ zIndex: 2600 }}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            maxWidth: 400,
            mx: 2,
          },
        }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' } } }}
      >
        {winnerEntry && (
          <Box
            sx={{
              textAlign: 'center',
              px: { xs: 3, sm: 4 },
              py: { xs: 3.5, sm: 4.5 },
              minHeight: 240,
              borderRadius: 3,
              background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              border: '2px solid rgba(255,215,0,0.4)',
              position: 'relative',
              overflow: 'visible',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.25,
            }}
          >
            <TrophyIcon sx={{ fontSize: 48, color: '#FFD700', filter: 'drop-shadow(0 0 12px rgba(255,215,0,0.6))' }} />

            {winnerPicture ? (
              <PrizeCircleImage
                src={winnerPicture}
                zoom={winnerEntry.modal_image_zoom ?? 1}
                rotation={winnerEntry.modal_image_rotation ?? 0}
                offsetX={winnerEntry.modal_image_offset_x ?? 0}
                offsetY={winnerEntry.modal_image_offset_y ?? 0}
                size={96}
              />
            ) : null}

            <Typography
              sx={{
                fontWeight: 800,
                color: '#FFD700',
                fontSize: { xs: '1.15rem', sm: '1.3rem' },
                lineHeight: 1.35,
                px: 1,
                wordBreak: 'break-word',
                textShadow: '0 0 12px rgba(255,215,0,0.25)',
              }}
            >
              {winnerName || (hotSeatPlayerName ? 'Prize' : getDisplayName(winnerEntry))}
            </Typography>

            {winnerEntry.email && (
              <Typography sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', fontSize: '0.8rem' }}>
                {winnerEntry.email}
              </Typography>
            )}

            <Typography
              sx={{
                color: 'rgba(255,255,255,0.85)',
                fontWeight: 700,
                fontSize: '0.85rem',
                letterSpacing: 1.2,
                mt: 0.5,
              }}
            >
              {hotSeatPlayerName ? 'PRIZE' : 'WINNER!'}
            </Typography>
            {hotSeatPlayerName ? (
              <Typography
                sx={{
                  color: 'rgba(255,215,0,0.9)',
                  display: 'block',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  lineHeight: 1.4,
                }}
              >
                For {hotSeatPlayerName}
              </Typography>
            ) : null}
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default LiveWheelDisplay;
